import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  tenantFormSchema,
  TenantFormData,
  defaultTenantFormValues,
  step1Schema,
  step2Schema,
  step3Schema,
} from "@/lib/schemas/tenant";
import { WizardProgress } from "@/components/agency/wizard/WizardProgress";
import { Step1BasicInfo } from "@/components/agency/wizard/Step1BasicInfo";
import { Step2ContactBilling } from "@/components/agency/wizard/Step2ContactBilling";
import { Step3PlanLimits } from "@/components/agency/wizard/Step3PlanLimits";
import { Step4Review } from "@/components/agency/wizard/Step4Review";

const STEPS = [
  { label: "Basic Info", description: "Business details" },
  { label: "Billing", description: "Contact & billing" },
  { label: "Plan", description: "Plan & limits" },
  { label: "Review", description: "Confirm & create" },
];

export default function NewCustomerPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<TenantFormData>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: defaultTenantFormValues,
    mode: "onChange",
  });

  const validateCurrentStep = async (): Promise<boolean> => {
    let isValid = false;
    
    switch (currentStep) {
      case 1:
        isValid = await form.trigger(["name", "slug", "domain"]);
        // Also check slug availability
        const slug = form.getValues("slug");
        if (isValid && slug) {
          try {
            const { data } = await supabase.functions.invoke("Calie_checkSlug", {
              body: { slug },
            });
            if (!data?.available) {
              form.setError("slug", { message: "This slug is already taken" });
              isValid = false;
            }
          } catch {
            // Continue if check fails
          }
        }
        break;
      case 2:
        isValid = await form.trigger(["billing_email", "billing_via_agency"]);
        break;
      case 3:
        isValid = await form.trigger(["plan_id", "max_users", "max_phone_numbers", "max_sms_per_day"]);
        break;
      case 4:
        isValid = true;
        break;
    }
    
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleEditStep = (step: number) => {
    setCurrentStep(step);
  };

  const handleSubmit = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      const values = form.getValues();
      
      const { data, error } = await supabase.functions.invoke("Calie_createTenant", {
        body: {
          name: values.name,
          slug: values.slug,
          domain: values.domain || null,
          billing_email: values.billing_email,
          billing_via_agency: values.billing_via_agency,
          plan_id: values.plan_id,
          max_users: values.max_users || null,
          max_phone_numbers: values.max_phone_numbers || null,
          max_sms_per_day: values.max_sms_per_day || null,
        },
      });

      if (error) throw error;

      toast.success("Customer created successfully!");
      navigate("/agency/customers");
    } catch (error: any) {
      console.error("Error creating tenant:", error);
      
      // Handle specific error cases
      if (error?.message?.includes("SLUG_TAKEN") || error?.message?.includes("duplicate")) {
        form.setError("slug", { message: "This slug is already taken" });
        setCurrentStep(1);
        toast.error("The slug is already in use. Please choose a different one.");
      } else {
        toast.error(error?.message || "Failed to create customer. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1BasicInfo form={form} />;
      case 2:
        return <Step2ContactBilling form={form} />;
      case 3:
        return <Step3PlanLimits form={form} />;
      case 4:
        return <Step4Review form={form} onEditStep={handleEditStep} />;
      default:
        return null;
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/agency/customers")}
              className="mb-4 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Customers
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Add New Customer</h1>
            <p className="text-muted-foreground mt-1">
              Set up a new customer account for your agency
            </p>
          </div>

          {/* Progress */}
          <WizardProgress currentStep={currentStep} steps={STEPS} />

          {/* Form */}
          <Form {...form}>
            <form onSubmit={(e) => e.preventDefault()}>
              <Card>
                <CardContent className="pt-6">
                  {renderStep()}
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex justify-between mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>

                {currentStep < 4 ? (
                  <Button type="button" onClick={handleNext}>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Customer"
                    )}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
    </MainLayout>
  );
}
